import { getEnv } from 'configs/env';
import {
  Button,
  CreateAlert,
  ITableStructureItem,
  Message,
  NumberUtils,
  ObjectUtils,
  Table,
} from 'modules';
import { moduleConfig } from 'modules/module.config';
import moment from 'moment';
import React, { FC, memo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

import { Routes } from '../../../AppRoutes';
import { Icon, InputSelect } from '../../../components';
import { InputDateTimeRange } from '../../../components/table-filter-inputs';
import { getLocaleKey } from '../../../languages';
import { AdminService } from '../../../services/admin';
import { withReportWraper } from '../wraper';

export const ReportSystem = withReportWraper(
  memo(
    () => {
      const navigate = useNavigate();
      const [rangeTime, setRangeTime] = useState({
        startTime: moment().startOf('month'), //Date.now() - 1000 * 60 * 60 * 24 * 30,
        endTime: moment().endOf('day'), //Date.now(),
      } as any);

      const [reports, setReports] = useState(null as any);
      const [systemReportDetail, setSystemReportDetail] = useState(null as any);
      const [modeOption, setModeOption] = useState('');

      const handleOnClickOrdersOverview = (element: any) => {
        navigate(
          Routes.OrdersOverviewPage.renderPath(
            moment(element?.start)?.startOf('day')?.toISOString(),
            moment(element?.start)
              ?.endOf('day')
              .subtract(1, 'day')
              .add(1, 'millisecond')
              ?.toISOString(),
          ),
        );
      };

      useEffect(() => {
        if (!rangeTime) {
          setReports(null);
        } else {
          setReports(null);

          AdminService.getSytemReport({
            fromDate: new Date(rangeTime.startTime),
            toDate: new Date(rangeTime.endTime),
          })
            .then((res) => {
              const sumReports = Object.keys(res[0]).reduce(
                (ouput: any, key: string) => {
                  //if (typeof res[0][key] === 'number') ouput[key] = res.reduce((sum: number, item: any) => sum + item[key], 0);
                  ouput[key] = res.reduce(
                    (sum: number, item: any) => sum + item[key],
                    0,
                  );
                  return ouput;
                },
                {},
              );

              setReports({
                count: res.length,
                data: res.sort((a: any, b: any) => b.start - a.start),
                sumReports,
              });
            })
            .catch((err) => setReports({ error: err.error }));
        }
      }, [rangeTime]);

      const ModeOptions = [
        { label: 'Balance', value: 'userBalance' },
        { label: 'Hot Wallet', value: 'hotWallet' },
        { label: 'Exchange', value: 'exchange' },
        { label: 'Convert', value: 'convert' },
        { label: 'Order', value: 'order' },
      ];

      const sumMiddleware =
        (func: (item: any) => any) =>
        (item: any, fetchData: any, column: ITableStructureItem) => {
          if (item.isSum) {
            if (column.key === 'date') return 'SUM';
            if (column.key === 'userCount') return reports?.data[0]?.userCount;
            if (column.key === 'userBalanceGp2')
              return NumberUtils.toFormatNumber(
                reports?.data[0]?.userBalanceGp2,
                +getEnv('NUMBER_DECIMAL_DISPLAY'),
              );
            if (column.key === 'hotWalletGp2')
              return NumberUtils.toFormatNumber(
                reports?.data[0]?.hotWalletGp2,
                +getEnv('NUMBER_DECIMAL_DISPLAY'),
              );
            if (column.key === 'hotWalletUsdtBep20')
              return NumberUtils.toFormatNumber(
                reports?.data[0]?.hotWalletUsdtBep20,
                +getEnv('NUMBER_DECIMAL_DISPLAY'),
              );
            if (column.key === 'hotWalletUsdtTrc20')
              return NumberUtils.toFormatNumber(
                reports?.data[0]?.hotWalletUsdtTrc20,
                +getEnv('NUMBER_DECIMAL_DISPLAY'),
              );
            if (
              column.key &&
              reports &&
              reports.sumReports &&
              typeof reports.sumReports[column.key] === 'number'
            ) {
              if (Number.isNaN(reports.sumReports[column.key])) return '--';
              const value = NumberUtils.toFormatNumber(
                +reports.sumReports[column.key] || 0,
                +getEnv('NUMBER_DECIMAL_DISPLAY'),
              );
              if (
                [
                  'userCount',
                  'userOrderCount',
                  'orderCount',
                  'loginCount',
                ].includes(column.key)
              )
                return `${(+reports.sumReports[column.key] || 0).toLocaleString(
                  getLocaleKey(),
                  {
                    maximumFractionDigits: 0,
                    minimumFractionDigits: 0,
                  },
                )}`;
              return `${value}`;
            }

            return '--';
          }

          return func(item);
        };

      const structure = [
        {
          name: 'Date',
          key: 'date',
          render: sumMiddleware((item) => {
            return (
              <div>
                {new Date(item.start).toLocaleDateString(getLocaleKey(), {
                  hour12: false,
                })}
              </div>
            );
          }),
          sort: {
            key: 'date',
          },
        },
        {
          name: 'Total Volume',
          key: 'orderVolume',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.orderVolume,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'orderVolume',
          },
        },
        {
          name: 'Users',
          key: 'userCount',
          render: sumMiddleware((item: any) => {
            return (+item.userCount).toLocaleString(getLocaleKey(), {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            });
          }),
          sort: {
            key: 'userCount',
          },
        },
        {
          name: 'Total User Orders',
          key: 'userOrderCount',
          render: sumMiddleware((item: any) => {
            return (
              <span
                className='orders-overview-clickable'
                onClick={() => handleOnClickOrdersOverview(item)}
              >
                {(+item.userOrderCount).toLocaleString(getLocaleKey(), {
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0,
                })}
              </span>
            );
          }),
          sort: {
            key: 'userOrderCount',
          },
        },
        {
          name: 'Volume Profit',
          key: 'profitVolume',
          className: 'profit-volume',
          render: sumMiddleware((item: any) => {
            return (
              <span
                className={
                  item?.profitVolume >= 0
                    ? 'profit-volume-green'
                    : 'profit-volume-red'
                }
              >
                {NumberUtils.toFormatNumber(
                  +item?.profitVolume,
                  +getEnv('NUMBER_DECIMAL_DISPLAY'),
                )}
              </span>
            );
          }),
          sort: {
            key: 'profitVolume',
          },
        },
        {
          name: 'User Balance USDT',
          key: 'userBalanceGp2',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.userBalanceGp2,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'userBalanceGp2',
          },
        },
        {
          name: 'Hot Wallet ()',
          key: 'hotWalletGp2',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.hotWalletGp2,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'hotWalletGp2',
          },
        },
        {
          name: 'Hot Wallet USDT BEP20',
          key: 'hotWalletUsdtBep20',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.hotWalletUsdtBep20,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'hotWalletUsdtBep20',
          },
        },
        {
          name: 'Hot Wallet USDT TRC20',
          key: 'hotWalletUsdtTrc20',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.hotWalletUsdtTrc20,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'hotWalletUsdtTrc20',
          },
        },
        {
          name: 'Exchange In',
          key: 'exchangeIn',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.exchangeIn,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'exchangeIn',
          },
        },
        {
          name: 'Exchange Out Raw',
          key: 'exchangeOutRaw',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.exchangeOutRaw,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'exchangeOutRaw',
          },
        },
        {
          name: 'Exchange Out',
          key: 'exchangeOut',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.exchangeOut,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'exchangeOut',
          },
        },
        {
          name: 'Exchange Profit',
          key: 'profitExchange',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.profitExchange,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'profitExchange',
          },
        },
        {
          name: 'Exchange Fee 1%',
          key: 'exchangeOutFeeTotal',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.exchangeOutFeeTotal,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'exchangeOutFeeTotal',
          },
        },
        {
          name: 'Refund USDT',
          key: 'refundValue',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.refundValue,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'refundValue',
          },
        },
        {
          name: 'Total Orders',
          key: 'orderCount',
          render: sumMiddleware((item: any) => {
            return (+item.orderCount).toLocaleString(getLocaleKey(), {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            });
          }),
          sort: {
            key: 'orderCount',
          },
        },
        {
          name: 'Login Count',
          key: 'loginCount',
          render: sumMiddleware((item: any) => {
            return (+item.loginCount).toLocaleString(getLocaleKey(), {
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            });
          }),
          sort: {
            key: 'loginCount',
          },
        },
        {
          name: 'Rank Commission',
          key: 'rankCommission',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.rankCommission,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'rankCommission',
          },
        },
        {
          name: 'Cash Back 0.2%',
          key: 'cashBack',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.cashBack,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'cashBack',
          },
        },
        {
          name: 'USDT Converted',
          key: 'convertRecieved',
          render: sumMiddleware((item: any) => {
            return NumberUtils.toFormatNumber(
              +item?.convertRecieved,
              +getEnv('NUMBER_DECIMAL_DISPLAY'),
            );
          }),
          sort: {
            key: 'convertRecieved',
          },
        },
      ].filter((v) => {
        if (modeOption === 'hotWallet')
          return ['date', 'hotWalletUsdtBep20', 'hotWalletUsdtTrc20'].includes(
            v.key,
          );
        if (modeOption === 'userBalance')
          return [
            'date',
            'userBalanceGp2',
            // "userBalanceUsdt",
          ].includes(v.key);
        if (modeOption === 'order')
          return [
            'date',
            'orderVolume',
            'orderCount',
            'userOrderCount',
            'profitVolume',
            'cashBack',
            'rankCommission',
            'refundValue',
          ].includes(v.key);
        if (modeOption === 'exchange')
          return [
            'date',
            'exchangeIn',
            'exchangeOutRaw',
            'profitExchange',
            'exchangeOut',
            'exchangeOutFeeTotal',
          ].includes(v.key);
        if (modeOption === 'convert')
          return ['date', 'convertRecieved'].includes(v.key);
        return true;
      });

      const handleExportExcel = async () => {
        return new Promise((resolve) => {
          try {
            const reportData = ObjectUtils.getIn(reports, 'data', []);
            const fileHead = structure.map((v) => v.name);
            const data = reportData.map((item: any) =>
              structure.map((column, index) => {
                if (index === 0)
                  return new Date(item['start']).toLocaleString(getLocaleKey());
                if (
                  [
                    'orderVolume',
                    'profitVolume',
                    // "userBalanceUsdt",
                    'userBalanceGp2',
                    'hotWalletUsdtGp2',
                    'hotWalletUsdtBep20',
                    'hotWalletUsdtTrc20',
                    'refundValue',
                    'exchangeIn',
                    'exchangeOut',
                    'exchangeOutRaw',
                    'exchangeOutFeeTotal',
                    'profitExchange',
                    'rankCommission',
                    'cashBack',
                    'convertRecieved',
                  ].includes(column?.key)
                )
                  return item[column.key]
                    ? NumberUtils.toFormatNumber(
                        +item[column.key],
                        +getEnv('NUMBER_DECIMAL_DISPLAY'),
                      )
                    : `0.00000000`;
                return item[column.key] || 0;
              }),
            );

            const dataExport = [
              fileHead,
              ...data,
              structure.map((column, index) => {
                if (index === 0) return 'SUM';
                if (column.key === 'date') return 'SUM';
                if (column.key === 'userCount')
                  return reports?.data[0]?.userCount;
                if (
                  [
                    // "userBalanceUsdt",
                    'userBalanceGp2',
                    'hotWalletUsdtGp2',
                    'hotWalletUsdtBep20',
                    'hotWalletUsdtTrc20',
                  ].includes(column?.key)
                )
                  return reports?.data[0][column.key]
                    ? NumberUtils.toFormatNumber(
                        +reports?.data[0][column.key],
                        +getEnv('NUMBER_DECIMAL_DISPLAY'),
                      )
                    : `0.00000000`;
                if (
                  [
                    'orderVolume',
                    'profitVolume',
                    'refundValue',
                    'exchangeIn',
                    'exchangeOut',
                    'exchangeOutRaw',
                    'exchangeOutFeeTotal',
                    'profitExchange',
                    'rankCommission',
                    'cashBack',
                    'convertRecieved',
                  ].includes(column?.key)
                )
                  return reports.sumReports[column.key]
                    ? NumberUtils.toFormatNumber(
                        +reports.sumReports[column.key],
                        +getEnv('NUMBER_DECIMAL_DISPLAY'),
                      )
                    : `0.00000000`;
                return reports.sumReports[column.key] || '0';
              }),
            ];

            const ws = XLSX.utils.aoa_to_sheet(dataExport);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'SheetJS');

            const now = new Date();
            XLSX.writeFile(
              wb,
              `System report ${now.toLocaleDateString().replace(/\//g, '-')} ${now
                .toLocaleTimeString()
                .replace(/:/g, '-')}.xlsx`,
              { type: 'binary' },
            );

            resolve(
              CreateAlert({
                type: 'success',
                message: 'Export data success.',
              }),
            );
          } catch (error: any) {
            resolve(
              CreateAlert({
                type: 'danger',
                message: error.message,
              }),
            );
          }
        });
      };

      return (
        <section className='box'>
          <div className='title'>System report</div>

          <SystemReportDetailPopup
            detail={systemReportDetail}
            onClose={() => setSystemReportDetail(null)}
          />

          <div className='SystemReport'>
            <div className='SystemReport__Head'>
              <div className='item'>
                <div className='label'>Range time</div>
                <InputDateTimeRange
                  onChange={(e) => setRangeTime(e)}
                  startTimeDefaultValue={rangeTime?.startTime}
                  endTimeDefaultValue={rangeTime?.endTime}
                  format={'MM-dd-y'}
                />
              </div>
              <div className='item'>
                <div className='label'>Mode</div>
                <InputSelect
                  options={ModeOptions}
                  onChange={(e) => setModeOption(e)}
                  onTouched={() => false}
                  value={modeOption}
                  name=''
                />
              </div>

              <div className='item'>
                <div className='label'>Export</div>
                <Button
                  label='Excel'
                  buttonType='success'
                  disabled={
                    !reports || (reports.data && reports.data.length === 0)
                  }
                  onClick={handleExportExcel}
                />
              </div>
            </div>

            {(() => {
              if (reports && !reports.data)
                return (
                  <Message
                    type='emptyData'
                    message={moduleConfig.translate('no-data')}
                  />
                );
              if (!rangeTime)
                return (
                  <Message
                    type='info'
                    message='You have to select range time to export report'
                  />
                );
              if (!reports) return <Message type='loading' />;
              if (reports.error)
                return <Message type='error' {...reports.error} />;

              return (
                <Table
                  doubleScroll
                  hasOrderColumn
                  className='overload hideFooter'
                  structure={structure}
                  fetchData={async (params) => {
                    let data = [...reports.data];

                    if (params['date'] === 'increase')
                      data = data.sort((a: any, b: any) => a.start - b.start);
                    if (params['date'] === 'descrease')
                      data = data.sort((a: any, b: any) => b.start - a.start);

                    if (params['orderVolume'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.orderVolume - b.orderVolume,
                      );
                    if (params['orderVolume'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.orderVolume - a.orderVolume,
                      );

                    if (params['userCount'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.userCount - b.userCount,
                      );
                    if (params['userCount'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.userCount - a.userCount,
                      );

                    if (params['userOrderCount'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.userOrderCount - b.userOrderCount,
                      );
                    if (params['userOrderCount'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.userOrderCount - a.userOrderCount,
                      );

                    if (params['profitVolume'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.profitVolume - b.profitVolume,
                      );
                    if (params['profitVolume'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.profitVolume - a.profitVolume,
                      );

                    // if (
                    //     params["userBalanceUsdt"] ===
                    //     "increase"
                    // )
                    //     data = data.sort(
                    //         (a: any, b: any) =>
                    //             a.userBalanceUsdt -
                    //             b.userBalanceUsdt
                    //     );
                    // if (
                    //     params["userBalanceUsdt"] ===
                    //     "descrease"
                    // )
                    //     data = data.sort(
                    //         (a: any, b: any) =>
                    //             b.userBalanceUsdt -
                    //             a.userBalanceUsdt
                    //     );

                    if (params['userBalanceGp2'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.userBalanceGp2 - b.userBalanceGp2,
                      );
                    if (params['userBalanceGp2'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.userBalanceGp2 - a.userBalanceGp2,
                      );

                    if (params['exchangeIn'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.exchangeIn - b.exchangeIn,
                      );
                    if (params['exchangeIn'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.exchangeIn - a.exchangeIn,
                      );
                    if (params['exchangeOutRaw'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.exchangeOutRaw - b.exchangeOutRaw,
                      );
                    if (params['exchangeOutRaw'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.exchangeOutRaw - a.exchangeOutRaw,
                      );

                    if (params['profitExchange'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.profitExchange - b.profitExchange,
                      );
                    if (params['profitExchange'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.profitExchange - a.profitExchange,
                      );

                    if (params['exchangeOut'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.exchangeOut - b.exchangeOut,
                      );
                    if (params['exchangeOutFeeTotal'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.exchangeOut - a.exchangeOut,
                      );

                    if (params['exchangeOutFeeTotal'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) =>
                          a.exchangeOutFeeTotal - b.exchangeOutFeeTotal,
                      );
                    if (params['exchangeOutFeeTotal'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) =>
                          b.exchangeOutFeeTotal - a.exchangeOutFeeTotal,
                      );

                    if (params['hotWalletUsdtBep20'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) =>
                          a.hotWalletUsdtBep20 - b.hotWalletUsdtBep20,
                      );
                    if (params['hotWalletUsdtBep20'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) =>
                          b.hotWalletUsdtBep20 - a.hotWalletUsdtBep20,
                      );

                    if (params['withdrawUsdtBep20'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) =>
                          a.withdrawUsdtBep20 - b.withdrawUsdtBep20,
                      );
                    if (params['withdrawUsdtBep20'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) =>
                          b.withdrawUsdtBep20 - a.withdrawUsdtBep20,
                      );

                    if (params['hotWalletUsdtTrc20'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) =>
                          a.hotWalletUsdtTrc20 - b.hotWalletUsdtTrc20,
                      );
                    if (params['hotWalletUsdtTrc20'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) =>
                          b.hotWalletUsdtTrc20 - a.hotWalletUsdtTrc20,
                      );

                    if (params['refundValue'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.refundValue - b.refundValue,
                      );
                    if (params['refundValue'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.refundValue - a.refundValue,
                      );

                    if (params['orderCount'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.orderCount - b.orderCount,
                      );
                    if (params['orderCount'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.orderCount - a.orderCount,
                      );

                    if (params['loginCount'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.loginCount - b.loginCount,
                      );
                    if (params['loginCount'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.loginCount - a.loginCount,
                      );

                    if (params['rankCommission'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.rankCommission - b.rankCommission,
                      );
                    if (params['rankCommission'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.rankCommission - a.rankCommission,
                      );

                    if (params['cashBack'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) => a.cashBack - b.cashBack,
                      );
                    if (params['cashBack'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) => b.cashBack - a.cashBack,
                      );
                    if (params['convertRecieved'] === 'increase')
                      data = data.sort(
                        (a: any, b: any) =>
                          a.convertRecieved - b.convertRecieved,
                      );
                    if (params['convertRecieved'] === 'descrease')
                      data = data.sort(
                        (a: any, b: any) =>
                          b.convertRecieved - a.convertRecieved,
                      );

                    return {
                      data: [{ isSum: true }, ...data],
                      count: data.length,
                    };
                  }}
                />
              );
            })()}
          </div>
        </section>
      );
    },
    () => true,
  ),
);

export const SystemReportDetailPopup: FC<{
  detail: any;
  onClose: () => void;
}> = (props) => {
  if (!props.detail) return null;

  const item = props.detail;

  const data = [
    {
      label: 'Start',
      value: `${new Date(item.start).toLocaleDateString(getLocaleKey(), {
        hour12: false,
      })} - ${new Date(item.start).toLocaleTimeString(getLocaleKey(), {
        hour12: false,
      })}`,
    },
    {
      label: 'End',
      value: `${new Date(item.end).toLocaleDateString(getLocaleKey(), {
        hour12: false,
      })} - ${new Date(item.start).toLocaleTimeString(getLocaleKey(), {
        hour12: false,
      })}`,
    },
    {
      label: 'Modified',
      value: `${new Date(item.modified).toLocaleDateString(getLocaleKey(), { hour12: false })} - ${new Date(
        item.start,
      ).toLocaleTimeString(getLocaleKey(), {
        hour12: false,
      })}`,
    },
    {
      label: 'Total won/lose volume',
      value: `${(+item.wonVolume).toLocaleString(getLocaleKey(), {
        maximumFractionDigits: 2,
      })}/${(+item.lostVolume).toLocaleString(getLocaleKey(), {
        maximumFractionDigits: 2,
      })}`,
    },
    {
      label: 'Hot Wallet',
      value: (
        <span>
          USDT:{' '}
          {(+item.hotWalletGp2).toLocaleString(getLocaleKey(), {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}{' '}
          <br />
          COMMISSION USDT:{' '}
          {(+item.hotWalletUsdt).toLocaleString(getLocaleKey(), {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    // {
    //     label: 'Deposit', value: <span>
    //         ETH: {(+item.depositEth).toLocaleString(getLocaleKey(), { maximumFractionDigits: 2, minimumFractionDigits: 2 })} <br />
    //         USDT: {(+item.depositUsdt).toLocaleString(getLocaleKey(), { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
    //     </span>
    // },
    {
      label: 'Withdraw',
      value: (
        <span>
          USDT:{' '}
          {(+item.withdrawGp2).toLocaleString(getLocaleKey(), {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}{' '}
          <br />
          COMMISSION USDT:{' '}
          {(+item.withdrawUsdt).toLocaleString(getLocaleKey(), {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      label: "User 's Balance",
      value: (
        <span>
          USDT:{' '}
          {(+item.userBalanceGp2).toLocaleString(getLocaleKey(), {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}{' '}
          {/* <br /> */}
          {/* USD: {(+item.userBalanceUsd).toLocaleString(getLocaleKey(), { maximumFractionDigits: 2, minimumFractionDigits: 2 })} <br /> */}
          {/* COMMISSION USDT:{" "} */}
          {/* {(+item.userBalanceUsdt).toLocaleString(getLocaleKey(), {
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                    })}{" "}
                    <br /> */}
          {/* BUT: {(+item.userBalanceBut).toLocaleString(getLocaleKey(), { maximumFractionDigits: 2, minimumFractionDigits: 2 })} */}
        </span>
      ),
    },
    {
      label: 'Safe',
      value: (
        <span>
          USDT:{' '}
          {(+item.safeGp2).toLocaleString(getLocaleKey(), {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}{' '}
          <br />
          COMMISSION USDT:{' '}
          {(+item.safeUsdt).toLocaleString(getLocaleKey(), {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    {
      label: 'Login Count',
      key: 'loginCount',
      value: item.loginCount,
    },
    {
      label: 'Direct Commission',
      key: 'directCommission',
      value: item.directCommission,
    },
    {
      label: 'CashBack',
      key: 'cashBack',
      value: (+item.cashBack).toLocaleString(getLocaleKey(), {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }),
    },
    {
      label: 'New Package',
      key: 'newPackage',
      value: item.newPackage,
    },
    {
      label: 'Trading Commission',
      key: 'rankCommission',
      value: item.rankCommission,
    },
  ];

  return (
    <div
      className='SystemReport__DetailPopup'
      id='SystemReport__DetailPopup'
      onClick={(e: any) =>
        e.target.id === 'SystemReport__DetailPopup' ? props.onClose() : null
      }
    >
      <div className='box'>
        <div className='boxTitle'>
          <span>Detail Report</span>
          <div className='btnClose' onClick={() => props.onClose()}>
            <Icon.Close />
          </div>
        </div>
        <div className='content'>
          <table>
            <tbody>
              {data.map((row, key) => (
                <tr key={key}>
                  <td>{row.label}</td>
                  <td>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
